import React, { ChangeEvent } from 'react';
import {
  Row, Col, Select,
} from 'nhsuk-react-components';
import { IRuleState, RuleActions } from 'reducers/rule/ruleReducer';
import useProtectRoute from 'hooks/useProtectRoute';
import { ActionTypes } from 'components/forms/Rule/Editors/ActionTypes';
import { CohortMetadata } from 'schemas/metadata/cohorts.ts';
import ComparatorControl from 'components/forms/Rule/Editors/DefaultEditor/ComparatorControl';

export interface DefaultEditorProps {
  ruleState: IRuleState;
  dispatch: React.Dispatch<RuleActions>;
  deriveDisplayName: (attributeName: string) => string;
  setOperator: (e: ChangeEvent<HTMLSelectElement>) => void;
  handleTextChange: (e: ChangeEvent<HTMLInputElement>, actionType: ActionTypes) => void;
  errors: { [key: string]: string };
  isComparatorRequired: () => boolean;
  teamCohorts: CohortMetadata[];
}

export function DefaultEditor({
  ruleState,
  dispatch,
  deriveDisplayName,
  setOperator,
  handleTextChange,
  errors,
  isComparatorRequired,
  teamCohorts,
}: DefaultEditorProps) {
  useProtectRoute({ validateAuthorisation: (authorisation) => authorisation.canCreateCustomRules() });

  return (
    <Row>
      <Col width="one-half">
        <Select
          id="rule-attribute"
          label="Choose attribute"
          onChange={(e: ChangeEvent<HTMLSelectElement>) => dispatch({ type: 'SET_ATTRIBUTE', payload: e.target.value })}
          value={ruleState.selectedAttribute.AttributeName}
        >
          {ruleState.availableAttributes
            .slice()
            .sort((a, b) => {
              const aLabel = deriveDisplayName(a.AttributeName);
              const bLabel = deriveDisplayName(b.AttributeName);
              return aLabel.localeCompare(bLabel);
            })
            .map((attributeOption: { AttributeName: string }) => (
              <Select.Option key={attributeOption.AttributeName} value={attributeOption.AttributeName}>
                {deriveDisplayName(attributeOption.AttributeName)}
              </Select.Option>
            ))}
        </Select>
      </Col>
      <Col width="one-half">
        <Select
          id="rule-operator"
          label="Choose operator"
          onChange={setOperator}
          value={ruleState.selectedOperator.Operator}
        >
          {ruleState.availableOperators
            .slice()
            .sort((a, b) => a.DisplayValue.localeCompare(b.DisplayValue))
            .map((operatorOption: { Operator: string; DisplayValue: string }) => (
              <Select.Option key={operatorOption.Operator} value={operatorOption.Operator}>
                {operatorOption.DisplayValue}
              </Select.Option>
            ))}
        </Select>
      </Col>
      <Col width="full" className="nhsuk-u-margin-bottom-4">
        <ComparatorControl
          ruleState={ruleState}
          dispatch={dispatch}
          handleTextChange={handleTextChange}
          errors={errors}
          isComparatorRequired={isComparatorRequired}
          teamCohorts={teamCohorts}
        />
      </Col>
    </Row>
  );
}
